/**
 * A sign-in in its own tab (slice 3b spec §5.2, §6.2): an app account's
 * connect or Reconnect, an MCP server's OAuth add, Try again or Reconnect,
 * and mock mode's consent page.
 *
 * - **Opened in the click** (`openSignInTab`), blank with "Opening sign-in…",
 *   so the popup blocker lets it through; the caller sends it to the link
 *   once `POST …` returns and the link passes `connectTarget`. Blocked, it's
 *   null and the caller goes in this tab, as before.
 * - **No opener:** it's dropped at once, so Composio's and Google's pages
 *   can't reach this tab. The two tabs talk only over a same-origin
 *   `BroadcastChannel`, which Google's opener policy can't cut either.
 * - **When the callback lands there** (`handOffSignIn`), the tab names the
 *   sign-in it was opened for (marked in its `sessionStorage` while it was
 *   blank) and sends the outcome; the tab that started it (`listenForSignIns`)
 *   answers, shows the banner (naming the connect it kept for that sign-in),
 *   and the sign-in tab closes. With nobody to
 *   answer it stays a normal Settings page; if the browser won't close it,
 *   the page says it can be closed.
 */
import type { Started } from "./integrations-api";

const SIGNIN_CHANNEL = "crew-integrations";

/** In the sign-in tab's own storage: which sign-in it was opened for. */
const SIGNIN_KEY = "crew.integrations.signin";
/** The connect it's for, so its banner can name the app if it stays (`readStarted`). */
const STARTED_KEY = "crew.integrations.started";

const PLACEHOLDER = "Opening sign-in…";

/** How long the sign-in tab waits for the tab that started it to answer. */
const ANSWER_MS = 1_500;
/** How long after `close()` the tab counts as one the browser wouldn't close. */
const CLOSE_MS = 300;

/** The callback's query, as the page reads it. */
export type SignInQuery = { connected: string | null; error: string | null; reason: string | null };

/** What a tab's `BroadcastChannel` does that this needs. */
export type Channel = {
  onmessage: ((e: MessageEvent) => void) | null;
  postMessage: (data: unknown) => void;
  close: () => void;
};

const channelOf = (): Channel => new BroadcastChannel(SIGNIN_CHANNEL);

type Message =
  | { type: "returned"; id: string; query: SignInQuery }
  | { type: "answered"; id: string };

const isQuery = (q: unknown): q is SignInQuery =>
  !!q && typeof q === "object" && ["connected", "error", "reason"].every((k) => k in q);

function messageOf(data: unknown): Message | null {
  if (!data || typeof data !== "object") return null;
  const m = data as Record<string, unknown>;
  if (typeof m.id !== "string") return null;
  if (m.type === "answered") return { type: "answered", id: m.id };
  if (m.type === "returned" && isQuery(m.query))
    return { type: "returned", id: m.id, query: m.query };
  return null;
}

/**
 * The sign-ins this tab opened, still to come back, each with the connect
 * it's for (null for an MCP server's): its banner names that one, however
 * sign-ins overlap (Devin on #254).
 */
const mine = new Map<string, Started | null>();

/** The tab a sign-in goes in: sent to its link, or closed when there's none. */
export type SignInTab = { id: string; go: (url: string) => void; close: () => void };

type Opened = {
  opener: unknown;
  sessionStorage: Pick<Storage, "setItem">;
  document: { title: string; body: { textContent: string | null } | null };
  location: { assign: (url: string) => void };
  close: () => void;
};

const randomId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * Opens the sign-in's tab now, in the click; null when the browser blocked
 * it. `started` is the connect it's for, when it's an app's.
 */
export function openSignInTab(
  deps: { open: () => Opened | null } = {
    open: () => window.open("", "_blank") as unknown as Opened | null,
  },
  started?: Started,
): SignInTab | null {
  const tab = deps.open();
  if (!tab) return null;
  const id = randomId();
  try {
    tab.document.title = PLACEHOLDER;
    if (tab.document.body) tab.document.body.textContent = PLACEHOLDER;
    tab.sessionStorage.setItem(SIGNIN_KEY, id);
    if (started) tab.sessionStorage.setItem(STARTED_KEY, JSON.stringify(started));
  } catch {
    /* it still signs in; it just won't close itself */
  }
  try {
    tab.opener = null;
  } catch {
    /* the browser already gave it none */
  }
  mine.set(id, started ?? null);
  return {
    id,
    go: (url) => tab.location.assign(url),
    close: () => {
      mine.delete(id);
      tab.close();
    },
  };
}

/**
 * In the tab that starts sign-ins: hears one of its own come back, answers so
 * that tab can close, and hands on the outcome. Returns its stop.
 */
export function listenForSignIns(
  onReturn: (query: SignInQuery, started: Started | null) => void,
  channel: () => Channel = channelOf,
): () => void {
  const ch = channel();
  ch.onmessage = (e) => {
    const m = messageOf(e.data);
    if (m?.type !== "returned" || !mine.has(m.id)) return;
    const started = mine.get(m.id) ?? null;
    mine.delete(m.id);
    ch.postMessage({ type: "answered", id: m.id } satisfies Message);
    onReturn(m.query, started);
  };
  return () => ch.close();
}

/**
 * What the sign-in tab did with its outcome: `none`, it isn't one (the
 * sign-in came back in the tab that started it); `closed`; `refused`, the
 * browser wouldn't close it; `alone`, nobody answered, so it stays.
 */
export type HandOff = "none" | "closed" | "refused" | "alone";

/** In the tab the callback landed in: tells the tab that started it, then closes. */
export async function handOffSignIn(
  query: SignInQuery,
  deps: {
    storage: Pick<Storage, "getItem" | "removeItem">;
    close: () => void;
    isClosed: () => boolean;
    channel?: () => Channel;
  },
): Promise<HandOff> {
  let id: string | null = null;
  try {
    id = deps.storage.getItem(SIGNIN_KEY);
    deps.storage.removeItem(SIGNIN_KEY);
  } catch {
    return "none";
  }
  if (!id) return "none";
  const ch = (deps.channel ?? channelOf)();
  const answered = await new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), ANSWER_MS);
    ch.onmessage = (e) => {
      const m = messageOf(e.data);
      if (m?.type !== "answered" || m.id !== id) return;
      clearTimeout(timer);
      resolve(true);
    };
    ch.postMessage({ type: "returned", id, query } satisfies Message);
  });
  ch.close();
  if (!answered) return "alone";
  deps.close();
  await new Promise((r) => setTimeout(r, CLOSE_MS));
  return deps.isClosed() ? "closed" : "refused";
}
